/** Canvas-drawn name tags, rendered as always-facing sprites. */
import * as THREE from 'three';
import { PALETTE } from '../palette';

export interface LabelOptions {
  /** pill background */
  bg?: string;
  fg?: string;
  /** world height of the sprite */
  height?: number;
  bold?: boolean;
}

export function makeLabel(text: string, opts: LabelOptions = {}): THREE.Sprite {
  const bg = opts.bg ?? 'rgba(255,255,255,0.92)';
  const fg = opts.fg ?? PALETTE.ink;
  const scale = 4; // supersample, then let the GPU downfilter
  const fontSize = 34 * scale;
  const padX = 20 * scale;
  const padY = 12 * scale;

  const measure = document.createElement('canvas').getContext('2d')!;
  const font = `${opts.bold === false ? 500 : 650} ${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
  measure.font = font;
  const textWidth = measure.measureText(text).width;

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(textWidth + padX * 2);
  canvas.height = Math.ceil(fontSize + padY * 2);
  const ctx = canvas.getContext('2d')!;
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  const r = canvas.height / 2;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.roundRect(0, 0, canvas.width, canvas.height, r);
  ctx.fill();
  ctx.strokeStyle = 'rgba(59,58,72,0.10)';
  ctx.lineWidth = 2 * scale;
  ctx.stroke();

  ctx.fillStyle = fg;
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 1 * scale);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;

  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false, depthTest: true }),
  );
  const h = opts.height ?? 0.34;
  sprite.scale.set((canvas.width / canvas.height) * h, h, 1);
  sprite.renderOrder = 10;
  sprite.userData.pickable = false;
  return sprite;
}
